import { createContext, useContext, useEffect, useRef } from "react";

export type UtilityPanel = "tasks" | "notifications" | "comments";

export interface OpenPanelOptions {
  replace?: boolean;
}

/**
 * Controls the single side panel (R1–R8). Entity content lives in the URL
 * (`?panel=<route>`); utility panels are local UI state and take turns with
 * it (R7).
 */
export interface PanelController {
  /** Route shown in the panel, e.g. "/people/123/bookings", or null. */
  panelPath: string | null;
  utility: UtilityPanel | null;
  open: (href: string, options?: OpenPanelOptions) => void;
  close: () => void;
  /** Makes the panel's content the main view (R2). */
  expand: () => void;
  /**
   * Opens `href` in the main view and keeps the panel open, e.g. a link
   * inside a signal trace that should stay beside the page it opens.
   */
  openInMain: (href: string) => void;
  openUtility: (utility: UtilityPanel) => void;
  closeUtility: () => void;
  /**
   * Runs `action` right away, or after the user confirmed discarding
   * unsaved panel changes (C7).
   */
  confirmDiscard: (action: () => void) => void;
  /** Registers a dirty flag for the panel content; returns an unregister. */
  registerDirty: (isDirty: () => boolean) => () => void;
}

export const PanelControllerContext = createContext<PanelController | null>(
  null,
);

export function usePanel() {
  const ctx = useContext(PanelControllerContext);
  if (!ctx) throw new Error("usePanel must be used inside MainLayout");
  return ctx;
}

export function useOptionalPanel() {
  return useContext(PanelControllerContext);
}

interface PanelScope {
  /** Depth of the panel's own history (R5); > 1 shows the back button. */
  depth: number;
}

/** Present only for content rendered inside the side panel. */
export const PanelScopeContext = createContext<PanelScope | null>(null);

/** True when the calling component renders inside the side panel. */
export function useInPanel() {
  return useContext(PanelScopeContext) !== null;
}

/**
 * Marks the current page as having unsaved changes. Inside the side panel,
 * closing, replacing or expanding the panel then asks first (C7). Also guards
 * reloads and closing the tab.
 */
export function useUnsavedChangesGuard(isDirty: boolean) {
  const panel = useOptionalPanel();
  const inPanel = useInPanel();
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;

  useEffect(() => {
    if (!inPanel || !panel) return;
    return panel.registerDirty(() => dirtyRef.current);
  }, [inPanel, panel]);

  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);
}
